/** Canonical platform setting keys (must match API `platform_settings.key`). */
export const PLATFORM_SETTING_KEYS = {
    AUTH_ALLOWLIST_ENFORCED: 'platform-auth-allowlist-enforced',
    AUTH_ALLOWLIST_EMAIL: 'platform-auth-allowlist-email',
    AUTH_ALLOWLIST_PHONE: 'platform-auth-allowlist-phone',
    AGE_VERIFICATION_ENABLED: 'platform-age-verification-enabled',
    AGE_VERIFICATION_AUTO_EMAIL_CHECK: 'platform-age-verification-auto-email-check',
    AGE_VERIFICATION_ACTIVE_PROVIDER: 'platform-age-verification-active-provider',
    AGE_VERIFICATION_VERIFYMY_ENV: 'platform-age-verification-verifymy-env',
    AGE_VERIFICATION_VERIFYMY_DEFAULT_BUSINESS_SETTINGS_ID: 'platform-age-verification-verifymy-default-business-settings-id',
    AGE_VERIFICATION_AGEMIN_ENV: 'platform-age-verification-agemin-env',
    AGE_VERIFICATION_AGEMIN_DEFAULT_ASSET_ID: 'platform-age-verification-agemin-default-asset-id',
    AGE_VERIFICATION_AGEMIN_EMAIL_ASSET_ID: 'platform-age-verification-agemin-email-asset-id',
    AGE_VERIFICATION_REQUIRED_MODE: 'platform-age-verification-required-mode',
    AGE_VERIFICATION_REQUIRED_JURISDICTIONS: 'platform-age-verification-required-jurisdictions',
    GEOFENCE_BLOCKED_JURISDICTIONS: 'platform-geofence-blocked-jurisdictions',
    GEOFENCE_LAW_LINKS: 'platform-geofence-law-links',
    NCMEC_CYBERTIPLINE_ENV: 'platform-ncmec-cybertipline-env',
    /** Whether non-admin identities may create new Spaces. Platform admins always can. */
    SPACE_CREATION_ENABLED: 'platform-space-creation-enabled',
};
export const PLATFORM_ROLE_VALUES = [
    'admin',
    'moderator',
    'support_agent',
];
export const PLATFORM_PERMISSION_VALUES = [
    'read-content-reports',
    'update-content-reports',
    'read-abuse-reports',
    'update-abuse-reports',
    'manage-escalated-reports',
    'read-support-tickets',
    'update-support-tickets',
    'manage-escalated-tickets',
    'view-admin-metrics',
    'manage-platform-settings',
    'manage-roles',
    'manage-users',
    'manage-identities',
];
// ---------------------------------------------------------------------------
// AdminApi class
// ---------------------------------------------------------------------------
export class AdminApi {
    client;
    constructor(client) {
        this.client = client;
    }
    async getMetrics() {
        return this.client.get('/api/admin/metrics');
    }
    async getPlatformSettings() {
        return this.client.get('/api/admin/platform-settings');
    }
    async getPlatformSetting(key) {
        return this.client.get(`/api/admin/platform-settings/${encodeURIComponent(key)}`);
    }
    async putPlatformSetting(key, body) {
        return this.client.put(`/api/admin/platform-settings/${encodeURIComponent(key)}`, body);
    }
    async listMaintenanceJobs() {
        return this.client.get('/api/admin/maintenance-jobs');
    }
    async startMaintenanceJob(jobId, body) {
        return this.client.post(`/api/admin/maintenance-jobs/${encodeURIComponent(jobId)}/start`, body);
    }
    async pauseMaintenanceJob(jobId) {
        return this.client.post(`/api/admin/maintenance-jobs/${encodeURIComponent(jobId)}/pause`, {});
    }
    async resumeMaintenanceJob(jobId) {
        return this.client.post(`/api/admin/maintenance-jobs/${encodeURIComponent(jobId)}/resume`, {});
    }
    async cancelMaintenanceJob(jobId) {
        return this.client.post(`/api/admin/maintenance-jobs/${encodeURIComponent(jobId)}/cancel`, {});
    }
    async getSanctionedCountries() {
        return this.client.get('/api/admin/sanctioned-countries');
    }
    async upsertSanctionedCountry(countryCode, body) {
        return this.client.put(`/api/admin/sanctioned-countries/${encodeURIComponent(countryCode)}`, body);
    }
    async runSanctionedCountriesSeed(mode) {
        return this.client.post('/api/admin/sanctioned-countries/seed', { mode });
    }
    /**
     * Registered age verification providers plus which one is active, so the
     * admin panel can validate the choice and warn about missing credentials.
     */
    async getAgeVerificationProviders() {
        return this.client.get('/api/admin/age-verification/providers');
    }
    async getJurisdictionRequirements() {
        return this.client.get('/api/admin/jurisdiction-requirements');
    }
    async updateJurisdictionVerificationConfig(jurisdiction, body) {
        return this.client.put(`/api/admin/jurisdiction-requirements/${encodeURIComponent(jurisdiction)}`, body);
    }
    async runJurisdictionRequirementsSeed(mode) {
        return this.client.post('/api/admin/jurisdiction-requirements/seed', { mode });
    }
    async listPlatformAdmins() {
        return this.client.get('/api/admin/platform-admins');
    }
    async listPlatformRoleHolders(role) {
        return this.client.get(`/api/admin/platform-roles/${encodeURIComponent(role)}`);
    }
    async grantPlatformRole(identityId, params) {
        return this.client.post(`/api/admin/identities/${encodeURIComponent(identityId)}/roles`, params);
    }
    async revokePlatformRole(identityId, role) {
        return this.client.delete(`/api/admin/identities/${encodeURIComponent(identityId)}/roles/${encodeURIComponent(role)}`);
    }
    async addPlatformAdmin(params) {
        const grant = await this.grantPlatformRole(params.identityId, { role: 'admin' });
        if (!grant.success) {
            return {
                success: false,
                error: grant.error,
            };
        }
        return this.listPlatformAdmins();
    }
    async removePlatformAdmin(identityId) {
        const revoke = await this.revokePlatformRole(identityId, 'admin');
        if (!revoke.success) {
            return {
                success: false,
                error: revoke.error,
            };
        }
        return this.listPlatformAdmins();
    }
    async grantPlatformAttribute(identityId, params) {
        return this.client.post(`/api/admin/identities/${encodeURIComponent(identityId)}/platform-attributes`, params);
    }
    async revokePlatformAttribute(identityId, attribute) {
        return this.client.delete(`/api/admin/identities/${encodeURIComponent(identityId)}/platform-attributes/${encodeURIComponent(attribute)}`);
    }
    // -------------------------------------------------------------------------
    // User management
    // -------------------------------------------------------------------------
    async searchUsers(query) {
        return this.client.get(`/api/admin/users/search?q=${encodeURIComponent(query)}`);
    }
    async getUserProfile(userId) {
        return this.client.get(`/api/admin/users/${encodeURIComponent(userId)}`);
    }
    async getUserSessions(userId) {
        return this.client.get(`/api/admin/users/${encodeURIComponent(userId)}/sessions`);
    }
    async getUserAuditLog(userId, params) {
        const searchParams = new URLSearchParams();
        if (params?.limit)
            searchParams.set('limit', String(params.limit));
        if (params?.offset)
            searchParams.set('offset', String(params.offset));
        const qs = searchParams.toString();
        return this.client.get(`/api/admin/users/${encodeURIComponent(userId)}/audit-log${qs ? `?${qs}` : ''}`);
    }
    async giftSubscription(userId, input) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/gift-subscription`, input);
    }
    async approveAge(userId) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/approve-age`, {});
    }
    async getEntitlements(userId) {
        return this.client.get(`/api/admin/users/${encodeURIComponent(userId)}/entitlements`);
    }
    async addEntitlement(userId, input) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/entitlements`, input);
    }
    async removeEntitlement(userId, entitlement) {
        return this.client.delete(`/api/admin/users/${encodeURIComponent(userId)}/entitlements/${encodeURIComponent(entitlement)}`);
    }
    async getSubscriptionOverrides(userId) {
        return this.client.get(`/api/admin/users/${encodeURIComponent(userId)}/subscription-overrides`);
    }
    async addSubscriptionOverride(userId, input) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/subscription-overrides`, input);
    }
    async updateSubscriptionOverride(userId, index, input) {
        return this.client.put(`/api/admin/users/${encodeURIComponent(userId)}/subscription-overrides/${index}`, input);
    }
    async removeSubscriptionOverride(userId, index) {
        return this.client.delete(`/api/admin/users/${encodeURIComponent(userId)}/subscription-overrides/${index}`);
    }
    async suspendUser(userId, input) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/suspend`, input);
    }
    async unsuspendUser(userId) {
        return this.client.delete(`/api/admin/users/${encodeURIComponent(userId)}/suspend`);
    }
    async banUser(userId, input) {
        return this.client.post(`/api/admin/users/${encodeURIComponent(userId)}/ban`, input);
    }
    async unbanUser(userId) {
        return this.client.delete(`/api/admin/users/${encodeURIComponent(userId)}/ban`);
    }
    // -------------------------------------------------------------------------
    // Identity management
    // -------------------------------------------------------------------------
    async searchIdentities(query) {
        return this.client.get(`/api/admin/identities/search?q=${encodeURIComponent(query)}`);
    }
    async getIdentityProfile(identityId) {
        return this.client.get(`/api/admin/identities/${encodeURIComponent(identityId)}`);
    }
    async getIdentitySessions(identityId) {
        return this.client.get(`/api/admin/identities/${encodeURIComponent(identityId)}/sessions`);
    }
    async getIdentityReports(identityId, params) {
        const searchParams = new URLSearchParams();
        if (params?.limit)
            searchParams.set('limit', String(params.limit));
        if (params?.page)
            searchParams.set('page', String(params.page));
        const qs = searchParams.toString();
        return this.client.get(`/api/admin/identities/${encodeURIComponent(identityId)}/reports${qs ? `?${qs}` : ''}`);
    }
    async getIdentityEntitlements(identityId) {
        return this.client.get(`/api/admin/identities/${encodeURIComponent(identityId)}/entitlements`);
    }
    async addIdentityEntitlement(identityId, input) {
        return this.client.post(`/api/admin/identities/${encodeURIComponent(identityId)}/entitlements`, input);
    }
    async removeIdentityEntitlement(identityId, entitlement) {
        return this.client.delete(`/api/admin/identities/${encodeURIComponent(identityId)}/entitlements/${encodeURIComponent(entitlement)}`);
    }
    async suspendIdentity(identityId, input) {
        return this.client.post(`/api/admin/identities/${encodeURIComponent(identityId)}/suspend`, input);
    }
    async unsuspendIdentity(identityId) {
        return this.client.delete(`/api/admin/identities/${encodeURIComponent(identityId)}/suspend`);
    }
    async banIdentity(identityId, input) {
        return this.client.post(`/api/admin/identities/${encodeURIComponent(identityId)}/ban`, input);
    }
    async unbanIdentity(identityId) {
        return this.client.delete(`/api/admin/identities/${encodeURIComponent(identityId)}/ban`);
    }
    // -------------------------------------------------------------------------
    // Site announcements
    // -------------------------------------------------------------------------
    async listAnnouncements() {
        return this.client.get('/api/admin/announcements');
    }
    async createAnnouncement(body) {
        return this.client.post('/api/admin/announcements', body);
    }
    async updateAnnouncement(id, body) {
        return this.client.put(`/api/admin/announcements/${encodeURIComponent(id)}`, body);
    }
    async toggleAnnouncementActive(id, active) {
        return this.client.patch(`/api/admin/announcements/${encodeURIComponent(id)}/active`, { active });
    }
    async deleteAnnouncement(id) {
        return this.client.delete(`/api/admin/announcements/${encodeURIComponent(id)}`);
    }
}
//# sourceMappingURL=admin-api.js.map